import axios from "axios";
import { useEffect, useState } from "react";
import { BookOpen, ChevronLeft, ChevronRight, X, Compass, ShieldAlert } from "lucide-react";
import { API } from "./shared";
import { GuideTour } from "./GuideTour";

const LABELS = { buyer: "Buyer", printer: "Printer", agency: "Agency", material_supplier: "Material Supplier", finishing_partner: "Finishing Partner" };
const block = (e) => e.preventDefault();

function GuideViewer({ role, onClose, onTour }) {
  const [sess, setSess] = useState(null);
  const [page, setPage] = useState(1);
  const [imgs, setImgs] = useState({});
  const [err, setErr] = useState("");
  useEffect(() => {
    axios.post(`${API}/guides/me/session`).then(r => setSess(r.data)).catch(e => setErr(e.response?.data?.detail || "Could not open the guide."));
  }, []);
  useEffect(() => {
    if (!sess) return;
    [page, page + 1].filter(n => n <= sess.pages && !imgs[n]).forEach(n =>
      axios.get(`${API}/guides/me/page/${n}`, { params: { session: sess.session_id }, responseType: "blob" })
        .then(r => setImgs(m => (m[n] ? m : { ...m, [n]: URL.createObjectURL(r.data) })))
        .catch(e => setErr(e.response?.status === 403 ? "Guide session expired. Please reopen the guide." : "Could not load this page.")));
  }, [sess, page, imgs]);
  useEffect(() => () => Object.values(imgs).forEach(u => URL.revokeObjectURL(u)), []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const b = document.querySelector(".guide-viewer-body"); if (b) b.scrollTop = 0; }, [page]);
  useEffect(() => {
    const k = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setPage(p => Math.min(sess?.pages || 1, p + 1));
      if (e.key === "ArrowLeft") setPage(p => Math.max(1, p - 1));
      if ((e.ctrlKey || e.metaKey) && ["s", "p", "c"].includes(e.key.toLowerCase())) e.preventDefault();
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [sess, onClose]);
  return (
    <div className="guide-viewer" data-testid="guide-viewer" onContextMenu={block} onCopy={block} onDragStart={block}>
      <header className="guide-viewer-bar">
        <b><BookOpen size={15}/> {LABELS[role]} user guide</b>
        <span className="guide-viewer-note"><ShieldAlert size={13}/> Confidential · personalised to your account · view only</span>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="secondary-btn" data-testid="guide-viewer-tour" onClick={onTour}><Compass size={14}/> <span className="hide-sm">Take the tour</span></button>
          <button className="secondary-btn" data-testid="guide-viewer-close" onClick={onClose} aria-label="Close guide"><X size={15}/></button>
        </div>
      </header>
      <div className="guide-viewer-body">
        {err ? <p data-testid="guide-viewer-error" style={{ color: "#fecaca" }}>{err}</p>
          : !imgs[page] ? <p style={{ color: "#cbd5e1" }} data-testid="guide-viewer-loading">Loading page…</p>
          : <div className="guide-page" data-testid={`guide-page-${page}`} style={{ backgroundImage: `url(${imgs[page]})` }} role="img" aria-label={`Guide page ${page}`}/>}
      </div>
      {sess && (
        <footer className="guide-viewer-nav">
          <button className="secondary-btn" data-testid="guide-viewer-prev" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={15}/> Previous</button>
          <span data-testid="guide-viewer-pageno">Page {page} of {sess.pages}</span>
          <button className="secondary-btn" data-testid="guide-viewer-next" disabled={page >= sess.pages} onClick={() => setPage(p => p + 1)}>Next <ChevronRight size={15}/></button>
        </footer>
      )}
    </div>
  );
}

export const GuideButton = ({ role, user, setView }) => {
  const [open, setOpen] = useState(false);
  const [tour, setTour] = useState(false);
  useEffect(() => {
    if (LABELS[role] && user && !user.tour_done && !sessionStorage.getItem("gpp_tour_seen")) {
      const t = setTimeout(() => { sessionStorage.setItem("gpp_tour_seen", "1"); setTour(true); }, 1500);
      return () => clearTimeout(t);
    }
  }, [role, user?.id, user?.tour_done]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!LABELS[role]) return null;
  const finishTour = () => { setTour(false); axios.post(`${API}/guides/tour-done`).catch(() => {}); };
  return (
    <>
      <button className="secondary-btn" onClick={() => setOpen(true)} data-testid="open-guide-button"
        title={`Open the ${LABELS[role]} user guide`} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <BookOpen size={14}/> <span className="hide-sm">{LABELS[role]} user guide</span>
      </button>
      <button className="secondary-btn" onClick={() => setTour(true)} data-testid="take-tour-button" title="Take a quick tour of your workspace"
        style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <Compass size={14}/> <span className="hide-sm">Tour</span>
      </button>
      {open && <GuideViewer role={role} onClose={() => setOpen(false)} onTour={() => { setOpen(false); setTour(true); }}/>}
      {tour && <GuideTour role={role} setView={setView} onDone={finishTour}/>}
    </>
  );
};
